import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import { ClassRoomsApi } from "@/openapi/generated";
import type { CreateClassRoomRequest } from "@/openapi/generated/models";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";

export const useClassRoomCreate = (
  toast: ReturnType<typeof import("@/components/feedback").useToast>,
) => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const { mutate, isPending, isSuccess, isError } = useMutation({
    mutationFn: async (createClassRoomRequest: CreateClassRoomRequest) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }

      const service = createAuthenticatedApi(ClassRoomsApi, accessToken);

      return service.apiClassRoomsPost({
        createClassRoomRequest,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.ClassRooms] });
      toast({
        title: "Class Room created",
        description: "Class Room created successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
    onError: (error: any) => {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to create class room";
      
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
  });

  return { mutate, isPending, isError, isSuccess };
};

